// Library Imports
import React, { useEffect, useState } from 'react';

// Local Imports
import { Alert, Button, TextField } from '../shared';
import { Link } from 'react-router-dom';
import assets from '../../assets';
import { validateEmail } from '../../utils/validator';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { registerUserAPI } from '../../redux/features/auth/authSlice';

const SignupForm = () => {
  const dispatch = useAppDispatch()
  const { loading, error, registerCurrUser } = useAppSelector((state) => state?.authUser);

  // States
  const [signupFormData, setSignupFormData] = useState<any>({
    userName: '',
    email: '',
    password: '',
  });
  const [isRequestSend, setIsRequestSend] = useState<boolean>(false);
  const [isShowAlert, setIsShowAlert] = useState<boolean>(false);
  const [emptyData, setEmptyData] = useState<any>([]);

  // Empty Check Fn
  const emptyCheck = () => {
    let isEmpty: any = {};
    Object.values(signupFormData).map((item, index) => {
      if (item === null || item === '') {
        isEmpty = {
          ...isEmpty,
          [Object.keys(signupFormData)[index]]: true,
        };
      }
      return item;
    });
    if (!validateEmail(signupFormData.email) && signupFormData.email !== '') {
      isEmpty = {
        ...isEmpty,
        isEmailValid: true,
      };
    }
    setEmptyData(isEmpty);

    return isEmpty ? (Object.keys(isEmpty).length === 0 ? false : true) : false;
  };

  // Handle FieldChange Fn
  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSignupFormData({ ...signupFormData, [name]: value });
    setEmptyData({
      ...emptyData,
      [name]: false,
      ...(name === 'email' && { isEmailValid: false, email: false }),
    });
    setIsShowAlert(false);
  };

  // Handle Sign In Fn
  const handleSignup = async () => {
    setIsShowAlert(false);
    if (!emptyCheck()) {
      const data = await dispatch(registerUserAPI({
        name: signupFormData.userName,
        email: signupFormData.email,
        password: signupFormData.password,
        role: 'admin'
      }));
      if (data?.meta?.requestStatus === 'rejected') {
        setIsShowAlert(true);
        return
      }
      setIsRequestSend(true);
    }
  };

  // UseEffect
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if ((event.code === "Enter" || event.code === "NumpadEnter") && !isRequestSend) {
        event.preventDefault();
        handleSignup();
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRequestSend, signupFormData]);

  return (
    <React.Fragment>
      <div className='h-full w-full flex flex-col items-center justify-center gap-3'>
        {/* LOGO */}
        <div className='w-[150px]'>
          <Link to='/'>
            <img src={assets.logo} className='object-fit' alt='Rylyf' />
          </Link>
        </div>

        {/* Form Section */}
        <section className='w-full max-w-md rounded-xl'>
          <div className='container flex items-center justify-center px-6 mx-auto'>
            <div className='w-full max-w-md'>
              {/* LOGO Name */}
              <div className='flex flex-col items-center justify-center'>
                {isRequestSend ?
                  <h1 className='text-2xl font-normal text-gray-800 capitalize sm:text-2xl'>
                    Account Created Successfully
                  </h1>
                  :
                  <h1 className='text-3xl font-normal text-gray-800 capitalize sm:text-3xl'>
                    sign up
                  </h1>}
              </div>

              <Alert color='danger' text={error ? error : 'Something went wrong'} showAlert={isShowAlert} setShowAlert={setIsShowAlert} />

              {/* Sign Up Fields */}
              {isRequestSend ?
                <div className='flex flex-col items-center justify-center'>
                  <p className='text-gray-700 mr-2 md:text-sm text-[14px]'>
                    {registerCurrUser ? registerCurrUser?.success?.data?.email : "Your account has been created successfully"}
                  </p>
                  <p className='text-base text-center text-gray-500 mt-1'>Please Check your email!</p>
                </div>
                :
                <div className='relative flex flex-col items-center mt-4 gap-4'>
                  <TextField
                    name={'userName'}
                    value={signupFormData.userName}
                    type='text'
                    placeholder='Name'
                    icon={true}
                    onChange={handleOnChange}
                    error={emptyData?.userName || isShowAlert ? true : false}
                    errorMsg={emptyData?.userName && 'Name is required'}
                  />
                  <TextField
                    name={'email'}
                    type='email'
                    value={signupFormData.email}
                    placeholder='Email address'
                    icon={true}
                    onChange={handleOnChange}
                    error={
                      emptyData?.email || emptyData?.isEmailValid || isShowAlert ? true : false
                    }
                    errorMsg={
                      (emptyData?.email && 'Email is required') ||
                      (emptyData?.isEmailValid && 'Email address is not valid')
                    }
                  />

                  <TextField
                    name={'password'}
                    value={signupFormData.password}
                    type='password'
                    placeholder='Password'
                    icon={true}
                    onChange={handleOnChange}
                    error={emptyData?.password || isShowAlert ? true : false}
                    errorMsg={emptyData?.password && 'Password is required'}
                  />
                </div>}

              {/* Buttons Section */}
              {isRequestSend ?
                <div className='mt-4'>
                  <Link to={'/login'}>
                    <Button variant='primary' fullWidth>
                      Done
                    </Button>
                  </Link>
                </div>
                :
                <div className='mt-4'>
                  <Button onClick={handleSignup} variant='primary' fullWidth loading={loading}>
                    Sign Up
                  </Button>

                  <div className='mt-4 text-center '>
                    <Link
                      to='/login'
                      className='text-sm text-blue-500 hover:underline'
                    >
                      Already have an account?
                    </Link>
                  </div>
                </div>}
            </div>
          </div>
        </section>
      </div>
    </React.Fragment>
  );
};

export default SignupForm;
